import DepthPaywall from "@/components/DepthPaywall";
import OfferImpression from "@/components/OfferImpression";
import ReportCollectInputsCTA from "@/components/ReportCollectInputsCTA";
import { reportPlanSummaryMap } from "@ishvaram/domain-data/report-plan-summaries";
import type { PremiumReportPlanId } from "@ishvaram/domain-data/report-plans";

/**
 * Intent-matched paid-report offer for calculators / reference pages whose matched
 * upgrade is a dedicated digital report. Every surface bridges to the birth-details
 * form (`/free-kundali` with the entered details carried in the hash), where the
 * result-page offer opens the real checkout.
 *
 * Why bridge-only (2026-08-12): the 2026-07-20 "sell directly when the calculator
 * already holds the birth details" inline checkout measured ZERO engagement —
 * 107 rashi seekers saw the inline form over 14 days with no taps — while the
 * bridge flow it replaced fed the site's highest-converting pipeline
 * (calculator → /free-kundali → kundali-result offer). Prefilled birth context
 * keeps the friction that motivated the inline experiment out of the bridge:
 * the seeker confirms their details, never re-types them. Going bridge-only also
 * removes the lazy `ssr:false` checkout chunk from every calculator page — a
 * ChunkLoadError there (rule 65's biggest live class) used to kill the money CTA.
 *
 * Price is read from the canonical generated plan map the Razorpay money path uses,
 * so it cannot drift from what is charged.
 *
 * Reach is metered by the render-once `OfferImpression` island (rule 46/47),
 * keyed on the stable plan/placement so it fires exactly once, matching the
 * `report_input_collection_started` navigation event the CTA emits.
 *
 * Must be listed in `ux_surface_budgets.yaml` monetization_components so the
 * budget guard counts it (single primary offer per surface).
 *
 * Copy is pain-led per rule 22 in rule-21 vocabulary; the caller passes the
 * matched headline/subhead for its report type.
 */
export default function CalculatorReportOfferCTA({
  planId,
  language = "en",
  sourcePath,
  placement,
  customerProblem,
  headline,
  subhead,
  name,
  birthDate,
  birthTime,
  birthPlace,
}: {
  planId: PremiumReportPlanId;
  language?: "en" | "hi";
  /** The page the offer is shown on (for accurate attribution). */
  sourcePath: string;
  /** Stable placement tag for the impression + click events. */
  placement: string;
  /** Short customer-problem tag (e.g. "gemstone", "baby_name", "lal_kitab"). */
  customerProblem: string;
  headline: string;
  subhead: string;
  name?: string;
  birthDate?: string;
  birthTime?: string;
  birthPlace?: string;
}) {
  const report = reportPlanSummaryMap[planId];
  const birth = { name, birthDate, birthTime, birthPlace };
  const collectInputsHref = reportInputHref({
    sourcePath,
    planId: report.id,
    language,
    birth,
  });
  const routeRankReason = `${customerProblem}_lookup_${report.id}`;
  const analyticsContext = {
    checkoutChannel: "ishvaram_report" as const,
    commerceSystem: "ishvaram" as const,
    intent: "chart_report",
    sourcePath,
    placement,
    customerProblem,
    urgency: "medium",
    confidence: 0.8,
    routeRankScore: 90,
    routeRankReason,
    rankedOfferOrder: [report.id],
  };

  return (
    <div className="mx-auto my-8 max-w-md px-4 sm:px-6">
      <OfferImpression
        planId={report.id}
        actionMode="input_collection"
        sourcePath={sourcePath}
        intent="chart_report"
        placement={placement}
        customerProblem={customerProblem}
        urgency="medium"
        confidence={0.8}
        routeRankScore={90}
        routeRankReason={routeRankReason}
        rankedOfferOrder={[report.id]}
      />
      <DepthPaywall planId={report.id} language={language}>
        <ReportCollectInputsCTA
          planId={report.id}
          priceInr={report.price_inr}
          headline={headline}
          subhead={subhead}
          language={language}
          collectInputsHref={collectInputsHref}
          collectInputsLabel={language === "hi" ? "Janam details bharein" : "Enter birth details"}
          collectInputsNote={collectInputsNote(language, Boolean(birthDate))}
          analyticsContext={analyticsContext}
        />
      </DepthPaywall>
    </div>
  );
}

type BirthInputs = {
  name?: string;
  birthDate?: string;
  birthTime?: string;
  birthPlace?: string;
};

function reportInputHref({
  sourcePath,
  planId,
  language,
  birth,
}: {
  sourcePath: string;
  planId: string;
  language: "en" | "hi";
  birth: BirthInputs;
}): string {
  const params: Record<string, string> = {
    intent: "chart_report",
    source: sourcePath,
    next_report: planId,
  };
  if (birth.name?.trim()) params.name = birth.name.trim();
  if (birth.birthDate) params.dob = birth.birthDate;
  if (birth.birthTime) params.time = birth.birthTime;
  if (birth.birthPlace?.trim()) params.place = birth.birthPlace.trim();
  const base = language === "hi" ? "/hi/free-kundali/" : "/free-kundali/";
  return `${base}#${new URLSearchParams(params).toString()}`;
}

function collectInputsNote(language: "en" | "hi", carriesDob: boolean): string {
  if (carriesDob) {
    return language === "hi"
      ? "Aapki bhari hui birth details carry over ho jaayengi — confirm karein, checkout khul jaayega."
      : "The birth details you entered carry over — confirm them and checkout opens.";
  }
  return language === "hi"
    ? "Pehle birth details bharein. Chart ready hote hi checkout khulega."
    : "Birth details come first. Checkout opens after the chart has the inputs needed for your report.";
}
